import { Input, Toggle } from '@playerbase/ui';
import type { RatingDisplay } from '@playerbase/types';

interface RatingConfigProps {
  display: RatingDisplay | undefined;
  onChange: (display: RatingDisplay | undefined) => void;
}

const DEFAULT_EMOJI = '⭐';

// Rating fields are always 1-5 (the 1-10 scale moved into the dedicated
// NPS kind in story 1). The studio's only choice is whether the radio
// options render as plain numbers (default) or the chosen emoji
// repeated n times.
export function RatingConfig({ display, onChange }: RatingConfigProps): JSX.Element {
  const useEmoji = display?.mode === 'emoji';
  const emoji = display?.mode === 'emoji' ? display.emoji : DEFAULT_EMOJI;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-text-muted">
        Players see <strong className="text-text">5 options from 1 to 5</strong>{' '}
        (5 at the top of the radio list).
      </p>

      <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2">
        <div className="flex flex-col">
          <span className="text-sm text-text">Show as emoji</span>
          <span className="text-xs text-text-muted">
            Replace the numbers with an emoji repeated n times.
          </span>
        </div>
        <Toggle
          checked={useEmoji}
          onCheckedChange={(checked) =>
            onChange(checked ? { mode: 'emoji', emoji: DEFAULT_EMOJI } : undefined)
          }
          aria-label={useEmoji ? 'Emoji mode on' : 'Emoji mode off'}
        />
      </div>

      {useEmoji ? (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-surface px-3 py-2">
          <label className="flex items-center gap-2">
            <span className="text-xs text-text-muted">Emoji</span>
            <Input
              value={emoji}
              onChange={(e) => {
                const next = e.target.value;
                onChange({
                  mode: 'emoji',
                  // Empty input falls back to the default rather than
                  // sending an invalid empty string to the RPC.
                  emoji: next.length === 0 ? DEFAULT_EMOJI : next,
                });
              }}
              maxLength={8}
              className="w-20 text-center"
              aria-label="Rating emoji"
            />
          </label>
          <div className="flex flex-col gap-1 pt-1">
            <span className="text-xs text-text-muted">Preview</span>
            {[5, 4, 3, 2, 1].map((n) => (
              <span
                key={n}
                className="rounded-sm border border-border bg-surface-2 px-2 py-0.5 text-xs text-text"
              >
                {emoji.repeat(n)}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
